import React from "react";
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const SignalTimeline = ({
    timelineId,
    selectedInstrument,
    availableInstruments,
    tickHistory,
    combinations,
    signalTypeFilter,
    strengthFilter,
    onRemoveTimeline,
    onInstrumentChange,
    getSignalForCombination,
    getSignalColor,
    formatTimestampShort,
    formatInstrumentDisplay
}) => {
    const handleInstrumentChange = (event) => {
        onInstrumentChange(timelineId, event.target.value);
    };

    return (
        <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
                        {/* <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
                            Timeline {timelineId}
                        </Typography> */}
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Instrument</InputLabel>
                            <Select
                                value={selectedInstrument || ''}
                                label="Instrument"
                                onChange={handleInstrumentChange}
                            >
                                <MenuItem value="">
                                    <em>No instrument selected</em>
                                </MenuItem>
                                {availableInstruments.map((instrument) => (
                                    <MenuItem key={instrument} value={instrument}>
                                        {formatInstrumentDisplay(instrument)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Tooltip title="Remove Timeline">
                        <IconButton 
                            size="small" 
                            onClick={() => onRemoveTimeline(timelineId)}
                            sx={{ ml: 1 }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                
                {selectedInstrument ? (
                    <TableContainer 
                        component={Paper} 
                        variant="outlined" 
                        sx={{ 
                            overflowX: 'auto',
                            flex: 1,
                            minHeight: 200
                        }}
                    >
                        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 150 + tickHistory.length * 25 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        position: 'sticky', 
                                        top: 0, 
                                        left: 0, 
                                        zIndex: 2, 
                                        width: 40, 
                                        backgroundColor: 'background.paper' 
                                    }}>
                                    </TableCell>
                                    {tickHistory.map((tick, index) => (
                                        <TableCell
                                            key={index}
                                            align="center"
                                            sx={{
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                minWidth: 16,
                                                backgroundColor: 'background.paper',
                                                fontSize: '0.5rem',
                                                padding: '2px 1px'
                                            }}
                                        >
                                            {(tick.timestamp.getSeconds() % 5 === 0) ? formatTimestampShort(tick.timestamp) : ''}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {combinations.map((combination) => (
                                    <TableRow key={combination}>
                                        <TableCell sx={{ 
                                            position: 'sticky', 
                                            left: 0, 
                                            zIndex: 1, 
                                            minWidth: 40, 
                                            backgroundColor: 'background.paper', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            padding: '2px 4px', 
                                            fontSize: '0.6rem',
                                            textAlign: 'center'
                                        }}>
                                            {combination.split(':')[1].split("_")[1]}
                                        </TableCell>
                                        {tickHistory.map((tick, index) => {
                                            const signal = getSignalForCombination(combination, tick.timestamp);
                                            if (!signal) {
                                                return (
                                                    <TableCell
                                                        key={index}
                                                        align="center"
                                                        sx={{
                                                            backgroundColor: '#f5f5f5',
                                                            minWidth: 10,
                                                            // minHeight: 10,
                                                            padding: 0,
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    />
                                                );
                                            }

                                            // Apply filters
                                            if (signalTypeFilter !== 'all' && signal.type !== signalTypeFilter) {
                                                return (
                                                    <TableCell
                                                        key={index}
                                                        align="center"
                                                        sx={{
                                                            backgroundColor: '#f5f5f5',
                                                            minWidth: 10,
                                                            // minHeight: 10,
                                                            padding: 0,
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    />
                                                );
                                            }

                                            if (strengthFilter !== 'all' && signal.strength !== strengthFilter) {
                                                return (
                                                    <TableCell
                                                        key={index}
                                                        align="center"
                                                        sx={{
                                                            backgroundColor: '#f5f5f5',
                                                            minWidth: 10,
                                                            // minHeight: 10,
                                                            padding: 0,
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    />
                                                );
                                            }

                                            return (
                                                <TableCell
                                                    key={index}
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: getSignalColor(signal),
                                                        minWidth: 10,
                                                        // minHeight: 10,
                                                        padding: 0,
                                                        border: '1px solid #e0e0e0'
                                                    }}
                                                />
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box 
                        sx={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            minHeight: 200,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            border: '2px dashed #ccc'
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Select an instrument to start streaming signals
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default SignalTimeline;
